import React from "react";
import { usePlanConfig } from "./PlanLadder";

/**
 * Multi-select checkbox picker for NMI plans.
 * Uses the authoritative plan_config from /api/plans/config so admins can only
 * pick real, currently-active plan names. Value is a comma-separated string
 * ("BASIC, PREMIER") — matches how nmi_plans is stored on facilities.
 *
 * Props:
 *  - value: string (comma-separated) OR null
 *  - onChange: (string|null) => void
 *  - testIdPrefix: optional prefix for per-option data-testid
 */
export const PlansMultiSelect = ({ value, onChange, testIdPrefix = "plan-picker" }) => {
  const cfg = usePlanConfig();
  const active = (cfg?.plans || []).filter((p) => p.active);
  const orderIndex = new Map(active.map((p, i) => [p.name.toUpperCase(), i]));

  const parseSelected = (v) => {
    if (!v || typeof v !== "string") return new Set();
    return new Set(
      v.split(",").map((t) => t.trim().toUpperCase()).filter(Boolean)
    );
  };

  const selected = parseSelected(value);

  const toggle = (name) => {
    const nextSet = new Set(selected);
    const key = name.toUpperCase();
    if (nextSet.has(key)) nextSet.delete(key); else nextSet.add(key);
    const ordered = [...nextSet].sort(
      (a, b) => (orderIndex.get(a) ?? 999) - (orderIndex.get(b) ?? 999)
    );
    onChange(ordered.length ? ordered.join(", ") : null);
  };

  if (!active.length) {
    return <div className="text-xs italic text-slate-500">Loading plans…</div>;
  }

  return (
    <div>
      <div className="flex flex-wrap gap-1.5">
        {active.map((p) => {
          const key = p.name.toUpperCase();
          const on = selected.has(key);
          return (
            <label
              key={key}
              data-testid={`${testIdPrefix}-${key.toLowerCase()}`}
              className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider transition-colors ${
                on
                  ? "border-nmi-700 bg-nmi-700 text-white"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              <input
                type="checkbox"
                checked={on}
                onChange={() => toggle(p.name)}
                className="h-3 w-3"
              />
              {p.name}
            </label>
          );
        })}
      </div>
      <div className="mt-1 text-[10px] text-slate-500">
        Selected: {selected.size ? [...selected].join(", ") : <em>none — will clear this field</em>}
      </div>
    </div>
  );
};
